import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import envConfig from '../../../envConfig';
import { sessionHash } from '../../../utility/remoteServiceUtil';

export const PUBLIC_DASHBOARD_HEADER = 'PUBLICDASHBOARDTOKEN';

const PUBLIC_DASHBOARD_PATH_REGEX = /\/p\/dashboard\/([^/?#]+)/i;

export const getPublicDashboardToken = () => {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(PUBLIC_DASHBOARD_PATH_REGEX);
  return match ? decodeURIComponent(match[1]) : null;
};

export const withPublicDashboardHeaders = (options = {}) => {
  const token = getPublicDashboardToken();
  if (!token) return options;
  return {
    ...options,
    headers: {
      ...(options.headers || {}),
      [PUBLIC_DASHBOARD_HEADER]: token,
    },
  };
};

// ---------------------------------------------------------------------------
// URL builders
// ---------------------------------------------------------------------------

const API_BASE = `${envConfig.apiUrl}/api/v1`;
const DASHBOARDS_URL = `${API_BASE}/dashboards`;
const WIDGET_DATA_URL = `${API_BASE}/widget-data`;

// ---------------------------------------------------------------------------
// One shared client + one place that adds the public-token header
// ---------------------------------------------------------------------------

const client = () => RaaAuthenticatedRemoteService.globalInstance();

// Thin wrappers so every dashboard call goes through the same header shim.
// Callers still get raw promises — error handling stays at the call site.
const http = {
  get: (url, options) => client().get(url, withPublicDashboardHeaders(options)),
  post: (url, options) => client().post(url, withPublicDashboardHeaders(options)),
  put: (url, options) => client().put(url, withPublicDashboardHeaders(options)),
  delete: (url, options) => client().delete(url, withPublicDashboardHeaders(options)),
};

// ---------------------------------------------------------------------------
// Response shaping helpers
// ---------------------------------------------------------------------------

/**
 * Flatten the API envelope { dashboard: { id, name, ... }, widgets: [...] }
 * into the flat shape the UI uses: { id, name, ..., widgets }.
 */
const fromApi = (payload) => {
  if (!payload) return null;
  const { dashboard, widgets, userPreferences } = payload;
  return {
    id: dashboard.id,
    name: dashboard.name,
    orgAccessLevel: dashboard.orgAccessLevel,
    publicToken: dashboard.publicToken,
    userPreferences: userPreferences || null,
    widgets: (widgets || []).map((w) => ({
      ...w,
      sizeX: w.sizeX,
      sizeY: w.sizeY,
    })),
  };
};

/**
 * Re-nest the flat UI shape into the payload the backend expects.
 */
const toApi = (dashboardData) => ({
  name: dashboardData.name,
  orgAccessLevel: dashboardData.orgAccessLevel ? dashboardData.orgAccessLevel : 0,
  widgets: (dashboardData.widgets || []).map((w) => ({
    id: typeof w.id === 'number' ? w.id : null,
    widgetType: typeof w.widgetTypeId === 'number' ? w.widgetTypeId : parseInt(w.widgetTypeId, 10),
    positionX: w.positionX,
    positionY: w.positionY,
    sizeX: w.sizeX,
    sizeY: w.sizeY,
    context: w.context ? w.context : null,
  })),
});

const unwrapDashboard = (response) => {
  if (!response) return fromApi(null);
  const envelope = (response.data && response.data.dashboard) || response.dashboard;
  return fromApi(envelope);
};

const unwrapBody = (response) => {
  if (!response) return null;
  return response.data ? response.data : response;
};

// ---------------------------------------------------------------------------
// Namespaces
// ---------------------------------------------------------------------------

const dashboards = {
  fetch: () => http
    .get(DASHBOARDS_URL)
    .then(unwrapDashboard),

  save: (id, dashboardData) => {
    const payload = toApi(dashboardData);
    const request = id
      ? http.put(`${DASHBOARDS_URL}/${id}`, { data: payload })
      : http.post(DASHBOARDS_URL, { data: payload });
    return request.then(unwrapDashboard);
  },

  regeneratePublicLink: (id) => http
    .post(`${DASHBOARDS_URL}/${id}/public-link`, { data: {} })
    .then((response) => response.publicToken),

  deletePublicLink: (id) => http.delete(`${DASHBOARDS_URL}/${id}/public-link`),
};

const alertsList = {
  getList: (widgetId, pageNo) => http.get(
    `${WIDGET_DATA_URL}/alerts-list/${widgetId}?pageNo=${pageNo}`,
  ),

  buildSamplesUrl: (widgetId, pageNo) => {
    const publicToken = getPublicDashboardToken();
    const authParam = publicToken
      ? `publicDashboardToken=${encodeURIComponent(publicToken)}`
      : `auth_token=${sessionHash}`;
    return `${WIDGET_DATA_URL}/alerts-list/${widgetId}/samples`
      + `?${authParam}`
      + `&pageNo=${pageNo}`;
  },
};

const heatIndex = {
  getChannel: (widgetId) => http.get(
    `${WIDGET_DATA_URL}/heat-index/channels/${widgetId}`,
  ),
};

const alertHistory = {
  getHistory: (widgetId, pageNo, pageSize) => http.get(
    `${WIDGET_DATA_URL}/alerts-history/${widgetId}?pageNo=${pageNo}&pageSize=${pageSize}`,
  ),
};

const sensorData = {
  getChannels: (widgetId) => http.get(
    `${WIDGET_DATA_URL}/sensor-data/${widgetId}/channels`,
  ),
  buildSamplesUrl: (widgetId) => {
    const publicToken = getPublicDashboardToken();
    const authParam = publicToken
      ? `publicDashboardToken=${encodeURIComponent(publicToken)}`
      : `auth_token=${sessionHash}`;
    return `${WIDGET_DATA_URL}/sensor-data/${widgetId}/samples`
      + `?${authParam}`;
  },
};

const statistics = {
  getStatistics: (widgetId) => http.get(
    `${WIDGET_DATA_URL}/statistics/${widgetId}`,
  ),
};

const channel = {
  getReading: (widgetId) => http.get(
    `${WIDGET_DATA_URL}/channel/${widgetId}`,
  ),
};

const deviceMap = {
  getData: (widgetId) => http.get(
    `${WIDGET_DATA_URL}/device-map-data/${widgetId}`,
  ),
};

const dashboardApi = {
  dashboards,
  alertsList,
  heatIndex,
  alertHistory,
  statistics,
  sensorData,
  deviceMap,
  unwrapBody,
  channel,
};

export default dashboardApi;
